// /src/renderer/features/settings/SettingsModal.tsx
import { useCallback, useEffect, useRef, useState } from 'react';
import Modal from '@components/Modal';
import SettingsTab from './tabs';
// import './SettingsModal.scss';
import styles from './SettingsModal.module.scss';

import {
  TAB_CONTENT_MAPPING,
  TabContentMappingProps,
} from './tabs/tabContentMapping';
import { useAuthPage } from '@features/auth/useAuthPage';
import { ICON_MAP } from '@components/Icons/Icons';

export interface SettingModalProps {
  initialTabId?: string;
  onClose: () => void;
}

const SettingModal = ({
  initialTabId = 'general',
  onClose,
}: SettingModalProps) => {
  const CloseIcon = ICON_MAP['close'];
  const { user } = useAuthPage();
  const isAdmin = user?.role?.name === 'admin';

  const resolveTabId = useCallback(
    (tabId?: string) => {
      const requested = tabId ?? 'general';
      if (requested === 'settings' && !isAdmin) return 'general';
      return TAB_CONTENT_MAPPING[requested] ? requested : 'general';
    },
    [isAdmin],
  );

  const [activeTab, setActiveTab] = useState<TabContentMappingProps>(
    TAB_CONTENT_MAPPING[resolveTabId(initialTabId)],
  );

  const contentRef = useRef<HTMLDivElement>(null);

  const handleTabChange = (tabId: string) => {
    const resolved = resolveTabId(tabId);
    setActiveTab(TAB_CONTENT_MAPPING[resolved]);
  };

  useEffect(() => {
    const resolved = resolveTabId(activeTab.id);
    if (resolved !== activeTab.id) {
      setActiveTab(TAB_CONTENT_MAPPING[resolved]);
    }
  }, [activeTab.id, resolveTabId]);

  // 1) 모달 열릴 때, 스크롤 점프 없이 content에 포커스 고정
  useEffect(() => {
    // requestAnimationFrame으로 레이아웃 잡힌 다음 포커스 주는게 안정적일 때가 많음
    requestAnimationFrame(() => {
      contentRef.current?.focus({ preventScroll: true });
    });
  }, []);

  // 2) 빈 공간 클릭 시 body로 포커스 빠지는 걸 막고, content로 포커스 유지
  const onContentMouseDownCapture = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;

      const focusable = target.closest(
        'input, textarea, select, button, a[href], [tabindex]:not([tabindex="-1"])',
      );

      // "진짜 컨트롤"을 누른 게 아니면(= 빈 공간 클릭)
      if (!focusable) {
        e.preventDefault(); // 이게 중요: body로 포커스 가는 걸 차단
        contentRef.current?.focus({ preventScroll: true });
      }
    },
    [],
  );

  return (
    <Modal
      type="page"
      close={onClose}
      description={
        <div className={styles.container}>
          <div className={styles.nav}>
            <SettingsTab activeTab={activeTab} setActiveTab={handleTabChange} />
          </div>
          <div
            ref={contentRef}
            tabIndex={-1}
            className={styles.content}
            onMouseDownCapture={onContentMouseDownCapture}
          >
            <button
              type="button"
              className={styles.contentCloseButton}
              onClick={onClose}
              aria-label="Close settings"
            >
              <CloseIcon className={styles.contentCloseIcon} />
            </button>
            <div className={styles.contentBody}>
              <activeTab.content />
            </div>
          </div>
        </div>
      }
    />
  );
};

export default SettingModal;
